/**
 * Semantic color tokens for in-app theming (issue #67).
 *
 * Screens migrate from the raw `COLORS` palette (src/utils/Constants.js) to
 * these role-based tokens; the full COLORS → token mapping table and the
 * migration recipe live in docs/DARK-MODE.md.
 *
 * Invariants:
 * - `lightTheme` values are byte-identical to today's COLORS values (same
 *   strings, same casing) so the light theme is pixel-identical and existing
 *   snapshots do not churn as screens migrate.
 * - `darkTheme` derives from the 2026 Brand Guidelines (DARK BLUE #242678,
 *   CYAN #00A5E3, WHITE, DARK GRAY #444444, SOFT BLUE #EDF9FF, ACCENT BLUE
 *   #B1EAFF). Every value was checked for WCAG 2.1 AA contrast against the
 *   surfaces it sits on; ratios are noted inline (checked with the standard
 *   relative-luminance formula).
 */

/** StatusBar bar-style — union literal, assignable to RN's StatusBarStyle. */
export type ThemeStatusBarStyle = 'dark-content' | 'light-content';

export interface ThemeColors {
  // Backgrounds
  /** Primary screen background. */
  readonly background: string;
  /** Grouped / secondary screen background (light: COLORS.greyBackground). */
  readonly backgroundMuted: string;
  /** Cards, list rows, sheets sitting on `background`. */
  readonly surface: string;
  /** Popups, menus, toasts — the highest elevation. */
  readonly surfaceElevated: string;
  /** Text inputs and form fields. */
  readonly inputBackground: string;
  /** Bottom tab bar background. */
  readonly tabBar: string;

  // Text
  /** Body text (light: COLORS.black #001533 — the app's "black"). */
  readonly textPrimary: string;
  /** Headings (light: COLORS.blueDarkText). */
  readonly textHeading: string;
  /** Secondary copy (light: COLORS.grey). */
  readonly textSecondary: string;
  /** Tertiary copy / metadata (light: COLORS.grey1). */
  readonly textTertiary: string;
  /** Muted copy, captions, inactive icons (light: COLORS.grey2). */
  readonly textMuted: string;
  /** Disabled text / placeholders (light: COLORS.grey3). */
  readonly textDisabled: string;
  /** Text and icons on accent/danger fills (filled buttons). */
  readonly textOnAccent: string;
  /** White label on saturated badge/pill fills in BOTH modes (status badges). */
  readonly textOnFill: string;
  /** Promo/benefit text ("Free For Me") — brand purple in light, soft white in dark. */
  readonly promoText: string;
  /** Favorites heart — brand magenta in light, brightened for dark surfaces. */
  readonly favorite: string;
  /** Inline links (light: COLORS.mainLargeText). */
  readonly link: string;
  /** 20%-alpha link wash — calendar range fills (light: COLORS.mainLargeText_20). */
  readonly linkSubtle: string;

  // Accent (brand blue family)
  /** Default accent — icons, selection states (light: COLORS.blueMain3). */
  readonly accent: string;
  /** Filled controls / primary buttons (light: COLORS.blueMain1). */
  readonly accentStrong: string;
  /** Pressed state of accent controls. */
  readonly accentPressed: string;
  /** 10%-alpha accent wash (light: COLORS.blueMain3_10). */
  readonly accentSubtle: string;
  /** 30%-alpha accent border (light: COLORS.blueMain3_30). */
  readonly accentBorder: string;
  /** Brand highlight (SOFT BLUE / ACCENT BLUE per 2026 guidelines). */
  readonly accentHighlight: string;

  // Status
  readonly success: string;
  readonly warning: string;
  readonly danger: string;
  /** 30%-alpha danger wash (light: COLORS.red_30). */
  readonly dangerMuted: string;
  /** Softer danger for large fills (light: COLORS.lightRed). */
  readonly dangerSoft: string;

  // Data visualization
  /** Chart series line — trend graphs (light: COLORS.blueMain2). */
  readonly chartSeries: string;
  /** Chart series area fill + data-point stroke (light: COLORS.blueGradient1). */
  readonly chartSeriesFill: string;

  // Structure
  /** Hairlines, dividers, input borders (light: COLORS.greyLight). */
  readonly border: string;
  /** Stronger border / disabled controls (light: COLORS.grey4). */
  readonly borderMuted: string;
  /** Most prominent card/row outlines (light: COLORS.grey3). */
  readonly borderStrong: string;
  /** Icon-tile borders (light: COLORS.iconBorderColor). */
  readonly iconBorder: string;
  /** Modal scrim (light: COLORS.black_70). */
  readonly overlay: string;
  /** Light scrim / pressed wash (light: COLORS.black_20). */
  readonly overlayLight: string;
  /** Image / card shadow color (light: COLORS.imageShadow). */
  readonly shadow: string;

  readonly transparent: string;
  /** StatusBar style matching the theme's background. */
  readonly statusBarStyle: ThemeStatusBarStyle;
}

/**
 * Today's palette, expressed semantically. Values are copied VERBATIM from
 * COLORS so a migrated screen renders pixel-identical in light mode.
 * (Do not "normalize" '#fff' to '#ffffff' — snapshots serialize the strings.)
 */
export const lightTheme: ThemeColors = {
  background: '#fff', // COLORS.white
  backgroundMuted: '#F7F8FC', // COLORS.greyBackground
  surface: '#fff', // COLORS.white
  surfaceElevated: '#fff', // COLORS.white
  inputBackground: '#fff', // COLORS.white
  tabBar: '#fff', // COLORS.white

  textPrimary: '#001533', // COLORS.black
  textHeading: '#090C39', // COLORS.blueDarkText
  textSecondary: '#27344E', // COLORS.grey
  textTertiary: '#4D5C71', // COLORS.grey1
  textMuted: '#5C697B', // COLORS.grey2
  textDisabled: '#8697AB', // COLORS.grey3
  textOnAccent: '#fff', // COLORS.white as button-title color
  textOnFill: '#FFFFFF', // badge labels stay white in both modes
  promoText: '#6900E0', // COLORS.purple
  favorite: '#C900E0', // legacy HeartIcon magenta
  link: '#3876AA', // COLORS.mainLargeText
  linkSubtle: '#3876AA33', // COLORS.mainLargeText_20

  accent: '#4AA6E1', // COLORS.blueMain3
  accentStrong: '#3D87BF', // COLORS.blueMain1
  accentPressed: '#3D87BF', // darker shade of accent = pressed (new role)
  accentSubtle: '#4AA6E11A', // COLORS.blueMain3_10
  accentBorder: '#4AA6E14D', // COLORS.blueMain3_30
  accentHighlight: '#EDF9FF', // brand SOFT BLUE (new role; unused pre-migration)

  success: '#23CE00', // COLORS.green
  warning: '#FF9900', // COLORS.orange
  danger: '#ED0000', // COLORS.red
  dangerMuted: '#ED00004d', // COLORS.red_30
  dangerSoft: '#FF5151', // COLORS.lightRed

  chartSeries: '#4499d2', // COLORS.blueMain2
  chartSeriesFill: '#6FA9D9', // COLORS.blueGradient1

  border: '#E0E4ED', // COLORS.greyLight
  borderMuted: '#C3CCD9', // COLORS.grey4
  borderStrong: '#8697AB', // COLORS.grey3
  iconBorder: '#D7DDE6', // COLORS.iconBorderColor
  overlay: '#001533B2', // COLORS.black_70
  overlayLight: '#00153333', // COLORS.black_20
  shadow: '#97ACBF', // COLORS.imageShadow

  transparent: 'transparent',
  statusBarStyle: 'dark-content',
};

/**
 * Dark palette — very-dark-blue family derived from brand DARK BLUE #242678
 * (hue held, lightness dropped so brand CYAN and light text clear WCAG AA).
 * Contrast ratios below are against background #101230 / surface #1A1D45.
 */
export const darkTheme: ThemeColors = {
  background: '#101230', // brand dark blue at ~7% lightness
  backgroundMuted: '#14173A', // one step up — grouped sections, tab bar
  surface: '#1A1D45', // cards/rows; textPrimary 15.0:1 on this
  surfaceElevated: '#232759', // popups/menus; nearest to brand #242678
  inputBackground: '#161A40', // between background and surface
  tabBar: '#14173A',

  textPrimary: '#EDF9FF', // brand SOFT BLUE — 17.0:1 / 15.0:1 (AAA)
  textHeading: '#EDF9FF', // headings join body color in dark (both AAA)
  textSecondary: '#C7CDE8', // desaturated blue-gray — 11.6:1 / 10.2:1
  textTertiary: '#A8B0D3', // 8.5:1 / 7.5:1
  textMuted: '#8F98C0', // 6.5:1 / 5.7:1 (AA)
  textDisabled: '#6F78A3', // 4.3:1 — disabled text is WCAG-exempt
  textOnAccent: '#001533', // navy on cyan fills — 6.5:1 (white was 2.8:1)
  textOnFill: '#FFFFFF', // white on red/green badge fills (RC device pass)
  promoText: '#EDF9FF', // purple unreadable on dark — primary-text white instead
  favorite: '#FF7BF3', // brightened magenta, reads on #101230/#1A1D45
  link: '#7CC4F0', // lightened link blue — 9.6:1 / 8.4:1
  linkSubtle: '#00A5E31F', // 12%-alpha cyan wash (matches accentSubtle on dark)

  accent: '#00A5E3', // brand CYAN passes as-is: 6.5:1 / 5.7:1 (AA)
  accentStrong: '#00A5E3', // filled buttons use brand cyan on dark
  accentPressed: '#33B7E8', // lighter = pressed feedback on dark
  accentSubtle: '#00A5E31F', // 12%-alpha cyan wash
  accentBorder: '#00A5E35C', // 36%-alpha cyan border
  accentHighlight: '#B1EAFF', // brand ACCENT BLUE — 14.0:1

  success: '#3CDD1F', // green lightened for dark — 10.0:1
  warning: '#FFAD33', // orange lightened — 9.8:1
  danger: '#FF5151', // red lightened (= light COLORS.lightRed) — 5.7:1 (AA)
  dangerMuted: '#FF515159', // 35%-alpha danger wash
  dangerSoft: '#FF7A7A', // 7.2:1

  chartSeries: '#33B7E8', // lightened series cyan — 6.9:1 surface / 7.9:1 bg
  chartSeriesFill: '#2076AD', // fill + dot stroke — 3.3:1 surface (≥3:1 non-text)

  border: '#2E3364', // hairlines readable on both bg and surface
  borderMuted: '#3A4070',
  borderStrong: '#3A3F6E', // prominent row outlines, one step past borderMuted
  iconBorder: '#1C2049', // subtle — at #2E3364 the IconButton gradient read as a stray crescent (RC device pass)
  overlay: '#000000BF', // pure-black scrim reads better over dark UIs
  overlayLight: '#00000066',
  shadow: '#000000', // shadows on dark are black, never blue-gray

  transparent: 'transparent',
  statusBarStyle: 'light-content',
};
